import { GlowCard } from "@/components/ui/spotlight-card";
import { ShineBorder } from "@/components/ui/shine-border";
import { TrustBar } from "./WhyUs/TrustBar";
import { TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { ShareloEmbed } from "@/components/ui/ShareloEmbed";

import { GrowthList } from "./WhyUs/GrowthList";
import { growthItems, type GrowthItem } from "./WhyUsSection.data";
import type { ReactNode } from "react";

interface WhyUsSectionProps {
  onCtaClick?: () => void;
  variant?: "default" | "hero";
  showTrustBar?: boolean;
  showRealExperience?: boolean;
  shareloId?: string;
  shareloTitle?: string;
  headline?: ReactNode;
  growthItems?: GrowthItem[];
  withDivider?: boolean;
  /** Defer the Sharelo player until it nears the viewport (below-the-fold usage). */
  deferVideo?: boolean;
}

export function WhyUsSection({
  onCtaClick,
  variant = "default",
  showTrustBar = true,
  showRealExperience = true,
  shareloId = "4XbnluOZ4qbUOqP2ig9X5",
  shareloTitle = "Why us — customer story",
  headline = (
    <>
      <span className="block sm:inline">What a great</span>{" "}
      <span className="block sm:inline">
        <span className="inline-flex items-baseline whitespace-nowrap sm:inline">
          website
          <span className="inline sm:hidden">{"\u00A0"}</span>
          <span className="hidden sm:inline"> </span>
          <span className="text-gradient-cycle align-baseline">actually does</span>
        </span>
      </span>
    </>
  ),
  growthItems: growthItemsProp = growthItems,
  withDivider = true,
  deferVideo = false,
}: WhyUsSectionProps = {}) {
  const isHero = variant === "hero";
  const realExperience = showRealExperience ? (
    <div className="flex items-center gap-3 px-0 sm:gap-4 sm:px-2">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-blue/10 sm:h-14 sm:w-14">
        <TrendingUp className="h-5 w-5 text-brand-blue sm:h-7 sm:w-7" strokeWidth={2.25} />
      </span>
      <div>
        <p className="text-lg font-bold uppercase tracking-tight text-brand-blue whitespace-nowrap sm:text-base sm:tracking-widest">
          20+ YEARS OF REAL EXPERIENCE
        </p>
        <p className="mt-1 text-sm text-muted-foreground sm:text-base">
          Built on our experience with brands pulling<span className="hidden lg:inline"><br /></span>{" "}
          <span className="font-bold text-foreground">
            hundreds of thousands of visitors a month.
          </span>
        </p>
      </div>
    </div>
  ) : null;
  return (
    <section className={cn("pt-10 pb-12 lg:pt-14 lg:pb-16", !isHero && "bg-background", isHero && "relative z-10")}>
      <div className="mx-auto max-w-7xl px-6 lg:px-10">

        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div className="flex flex-col gap-3 sm:gap-6">
            <ShareloEmbed id={shareloId} title={shareloTitle} preload={!deferVideo} deferUntilVisible={deferVideo} />
            {showTrustBar && (
              <>
                <div className="my-6 hidden h-px w-full bg-gradient-to-r from-transparent via-foreground/15 to-transparent sm:block" />
                <div className="mt-[10px] flex justify-center sm:mt-0">
                  <TrustBar onCtaClick={onCtaClick} showAvatars={false} />
                </div>
              </>
            )}
            {realExperience && (
              <div className="mt-2 sm:hidden">{realExperience}</div>
            )}
          </div>

          <div className="flex flex-col gap-8">
            <ShineBorder
              borderRadius={16}
              borderWidth={2}
              duration={10}
              color={["#3b82f6", "#a855f7", "#ec4899"]}
              className="w-full max-w-[32rem]"
            >
              <GlowCard glowColor="blue" disableInnerGlow customSize className="relative w-full bg-card">
                <div className="py-4 px-6 sm:py-5 sm:px-8">
                  <h3 className="text-center text-[1.7rem] font-bold tracking-tight text-foreground sm:text-[1.7rem]">
                    {headline}
                  </h3>
                  <GrowthList items={growthItemsProp} />
                </div>
              </GlowCard>
            </ShineBorder>
            {realExperience && (
              <div className="hidden sm:block">
                <div className="my-[19px] h-px w-full bg-gradient-to-r from-transparent via-foreground/15 to-transparent" />
                {realExperience}
              </div>
            )}
          </div>
        </div>
      </div>
      {withDivider && (
        <div className="mx-auto max-w-7xl px-6 pt-12 sm:pt-16 lg:px-10">
          <div className="h-px w-full bg-gradient-to-r from-transparent via-foreground/15 to-transparent" />
        </div>
      )}
    </section>

  );
}

